/**
 * SIGHTSS — add-to-cart feedback
 * ---------------------------------------------------------------------------
 * The storefront uses the custom `sections/header-mega.liquid` header, which
 * renders its own cart link instead of `snippets/header-actions.liquid`. As a
 * result none of the stock cart machinery is on the page: `cart-icon.js` never
 * loads, `snippets/cart-bubble.liquid` is never rendered, and — even though
 * `settings.cart_type` is `drawer` — no `<cart-drawer-component>` exists to be
 * opened. On top of that, the product page upsell block adds items with a raw
 * `fetch('/cart/add.js')` and then dispatches `cart:refresh` on `window`, an
 * event nothing in the theme listens for.
 *
 * This script closes both gaps without duplicating theme logic:
 *
 *   1. It listens for the theme's own `cart:update` event first (dispatched by
 *      `product-form.js` via `CartAddEvent`, see `assets/events.js`).
 *   2. It also listens for the upsell block's `cart:refresh`, and — as a last
 *      resort — observes completed `fetch`/`XMLHttpRequest` calls to the Cart
 *      Ajax API, so any add path is covered.
 *   3. Whatever the trigger, it re-reads `/cart.js` for the authoritative item
 *      count rather than guessing, then updates every `[data-cart-count]` in
 *      the header.
 *   4. For confirmation it prefers the theme's own mechanism: if a
 *      `<cart-drawer-component>` is present it opens that. Only when there is
 *      no drawer does it fall back to an accessible toast + count animation.
 *
 * Dependency-free, idempotent, and a no-op when the elements are absent.
 * All motion is suppressed under `prefers-reduced-motion: reduce`.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__sightssCartFeedback) return;
  window.__sightssCartFeedback = true;

  /* Captured before we patch `window.fetch`, so our own reads stay unhooked. */
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  var COUNT_SELECTOR = '[data-cart-count]';
  var CART_LINK_SELECTOR = '.ek-cart';
  var STORAGE_KEY = 'cart-count';
  var TOAST_ID = 'sightss-cart-toast';
  var TOAST_TIMEOUT = 4500;
  var SETTLE_DELAY = 80;

  /* ---------------------------------------------------------------------- */
  /* Small helpers                                                          */
  /* ---------------------------------------------------------------------- */

  function reducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_) {
      return false;
    }
  }

  function countNodes() {
    try {
      return document.querySelectorAll(COUNT_SELECTOR);
    } catch (_) {
      return [];
    }
  }

  /** Current count as shown in the header, used only as a fallback. */
  function displayedCount() {
    var nodes = countNodes();
    if (!nodes || !nodes.length) return 0;
    var parsed = parseInt((nodes[0].textContent || '').trim(), 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  /** The cart URL, taken from the header link so locale prefixes survive. */
  function cartUrl() {
    var link = document.querySelector(CART_LINK_SELECTOR);
    var href = link && link.getAttribute ? link.getAttribute('href') : null;
    return href || '/cart';
  }

  /** Root-relative path of a request URL, or '' if it is cross-origin. */
  function samePath(input) {
    var raw = '';

    try {
      if (typeof input === 'string') raw = input;
      else if (input && typeof input.url === 'string') raw = input.url;
      else if (input && typeof input.href === 'string') raw = input.href;
      else if (input != null) raw = String(input);

      if (!raw) return '';

      var url = new URL(raw, window.location.href);
      if (url.origin !== window.location.origin) return '';
      return url.pathname;
    } catch (_) {
      return typeof raw === 'string' && raw.charAt(0) === '/' ? raw.split('?')[0] : '';
    }
  }

  /** Cart Ajax endpoints we care about. Returns 'add', 'change' or ''. */
  function classifyCartPath(path) {
    if (!path) return '';
    if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
    if (/\/cart\/(change|update|clear)(\.js)?$/.test(path)) return 'change';
    return '';
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering the count                                                    */
  /* ---------------------------------------------------------------------- */

  function bump(node) {
    var button = node.closest ? node.closest(CART_LINK_SELECTOR) : null;
    if (!button || reducedMotion()) return;

    /* Restart the animation even on rapid successive adds. */
    button.classList.remove('is-adding');
    void button.offsetWidth;
    button.classList.add('is-adding');
    window.setTimeout(function () {
      button.classList.remove('is-adding');
    }, 700);
  }

  function renderCount(count, animate) {
    var value = String(count);
    var nodes = countNodes();

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      /* Set the number before revealing it, so it is never briefly stale. */
      node.textContent = value;
      node.classList.toggle('is-empty', count === 0);
      if (animate && count > 0) bump(node);
    }

    /*
     * Keep the stock cart bubble in step too, but only when `cart-icon.js` is
     * not on the page — when it is, that component owns the element and we
     * must not fight it.
     */
    if (!(window.customElements && customElements.get('cart-icon'))) {
      var bubbleCounts = document.querySelectorAll('.cart-bubble__text-count');
      for (var j = 0; j < bubbleCounts.length; j++) {
        bubbleCounts[j].textContent = count < 100 ? value : '';
        bubbleCounts[j].classList.toggle('hidden', count === 0);
      }
      var bubbles = document.querySelectorAll('.cart-bubble');
      for (var k = 0; k < bubbles.length; k++) {
        bubbles[k].classList.toggle('visually-hidden', count === 0);
      }
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ value: value, timestamp: Date.now() }));
    } catch (_) {
      /* Private mode / blocked storage — non-fatal. */
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Confirmation                                                           */
  /* ---------------------------------------------------------------------- */

  /**
   * Prefer the theme's own drawer when one is rendered on the page.
   * @returns {boolean} true when the drawer handled the confirmation.
   */
  function openThemeDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;

    try {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      if (typeof drawer.showDialog === 'function') {
        drawer.showDialog();
        return true;
      }
    } catch (_) {
      /* Fall through to the toast. */
    }

    return false;
  }

  var toastTimer = null;

  function dismissToast() {
    var toast = document.getElementById(TOAST_ID);
    if (!toast) return;
    toast.classList.remove('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, reducedMotion() ? 0 : 260);
  }

  function buildToast() {
    var toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.className = 'sightss-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');

    var icon = document.createElement('span');
    icon.className = 'sightss-cart-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '✓';

    var message = document.createElement('span');
    message.className = 'sightss-cart-toast__message';
    message.setAttribute('data-sightss-toast-message', '');

    var link = document.createElement('a');
    link.className = 'sightss-cart-toast__link';
    link.setAttribute('href', cartUrl());
    link.textContent = 'View cart';

    var close = document.createElement('button');
    close.className = 'sightss-cart-toast__close';
    close.setAttribute('type', 'button');
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', dismissToast);

    toast.appendChild(icon);
    toast.appendChild(message);
    toast.appendChild(link);
    toast.appendChild(close);

    return toast;
  }

  function showToast(count) {
    var toast = document.getElementById(TOAST_ID);

    if (!toast) {
      toast = buildToast();
      if (!document.body) return;
      document.body.appendChild(toast);
    }

    var message = toast.querySelector('[data-sightss-toast-message]');
    if (message) {
      message.textContent =
        count === 1 ? 'Added to cart — 1 item' : 'Added to cart — ' + count + ' items';
    }

    var link = toast.querySelector('.sightss-cart-toast__link');
    if (link) link.setAttribute('href', cartUrl());

    /* Let the element land in the DOM before transitioning it in. */
    window.requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(dismissToast, TOAST_TIMEOUT);
  }

  function confirm(count) {
    if (openThemeDrawer()) return;
    showToast(count);
  }

  /* ---------------------------------------------------------------------- */
  /* Reading the true cart state                                            */
  /* ---------------------------------------------------------------------- */

  var pending = null;
  var pendingIsAdd = false;

  /**
   * Ask the Cart Ajax API for the real item count. Never guesses.
   * @returns {Promise<number|null>}
   */
  function readCart() {
    if (!nativeFetch) return Promise.resolve(null);

    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? String(window.Shopify.routes.root).replace(/\/$/, '')
        : '';

    return nativeFetch(root + '/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response || !response.ok) return null;
        return response.json();
      })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return null;
        return cart.item_count;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Coalesce every signal that arrives for a single user action into one
   * `/cart.js` read and one confirmation.
   * @param {boolean} isAdd Whether this signal represents an item being added.
   */
  function schedule(isAdd) {
    pendingIsAdd = pendingIsAdd || !!isAdd;

    if (pending) return;

    pending = window.setTimeout(function () {
      pending = null;
      var wasAdd = pendingIsAdd;
      pendingIsAdd = false;

      readCart().then(function (count) {
        if (count === null) {
          /*
           * The cart could not be read (offline, blocked request). Do not
           * invent a number — leave the header alone rather than lie about it.
           */
          return;
        }

        renderCount(count, wasAdd);

        if (wasAdd && count > 0) confirm(count);
      });
    }, SETTLE_DELAY);
  }

  /* ---------------------------------------------------------------------- */
  /* Signals                                                                */
  /* ---------------------------------------------------------------------- */

  /* 1. The theme's own event (assets/events.js → ThemeEvents.cartUpdate). */
  document.addEventListener('cart:update', function (event) {
    var detail = (event && event.detail) || {};
    var data = detail.data || {};
    if (data.didError) return;
    schedule(data.source === 'product-form-component');
  });

  /* 2. The product-page upsell block dispatches this on `window`. */
  window.addEventListener('cart:refresh', function () {
    schedule(true);
  });

  /* 3. Fallback: watch the Cart Ajax API directly. */
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var kind = '';

      try {
        var method =
          (init && init.method) ||
          (input && typeof input === 'object' && input.method) ||
          'GET';
        if (String(method).toUpperCase() === 'POST') {
          kind = classifyCartPath(samePath(input));
        }
      } catch (_) {
        kind = '';
      }

      var result = nativeFetch(input, init);

      if (kind) {
        result.then(
          function (response) {
            if (response && response.ok) schedule(kind === 'add');
          },
          function () {
            /* Request failed — nothing was added, so say nothing. */
          }
        );
      }

      return result;
    };
  }

  if (typeof window.XMLHttpRequest === 'function') {
    var XHR = window.XMLHttpRequest.prototype;
    var nativeOpen = XHR.open;
    var nativeSend = XHR.send;

    if (typeof nativeOpen === 'function' && typeof nativeSend === 'function') {
      XHR.open = function (method, url) {
        try {
          this.__sightssCartKind =
            String(method).toUpperCase() === 'POST' ? classifyCartPath(samePath(url)) : '';
        } catch (_) {
          this.__sightssCartKind = '';
        }
        return nativeOpen.apply(this, arguments);
      };

      XHR.send = function () {
        var self = this;
        if (self.__sightssCartKind) {
          self.addEventListener('loadend', function () {
            if (self.status >= 200 && self.status < 300) {
              schedule(self.__sightssCartKind === 'add');
            }
          });
        }
        return nativeSend.apply(self, arguments);
      };
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Back/forward cache — keep the count honest without animating.          */
  /* ---------------------------------------------------------------------- */

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    try {
      var stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      var parsed = JSON.parse(stored);
      if (Date.now() - parsed.timestamp >= 10000) return;

      var count = parseInt(parsed.value, 10);
      if (!isNaN(count) && count >= 0 && count !== displayedCount()) {
        renderCount(count, false);
      }
    } catch (_) {
      /* no-op */
    }
  });
})();
